import { ClipboardList, Receipt, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import type { Order } from "../../../types/api";
import { formatCompactDate, formatCurrency, formatDateTime } from "../../../utils/format";

const paymentBadge: Record<string, string> = {
  paid: "ego-badge ego-badge-success",
  successful: "ego-badge ego-badge-success",
  pending: "ego-badge ego-badge-warning",
  failed: "ego-badge ego-badge-danger",
  refunded: "ego-badge ego-badge-neutral",
};

function badgeFor(status?: string) {
  if (!status) return "ego-badge ego-badge-neutral";
  return paymentBadge[status.toLowerCase()] ?? "ego-badge ego-badge-neutral";
}

function initials(name?: string) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function registrationEntries(order: Order) {
  const responses = order.registrationForm?.responses;
  if (!responses) return [];

  return [
    ["Full Name", responses.fullName],
    ["Gender", responses.gender],
    ["Phone number", responses.phone],
    ["Email address", responses.email],
    ["Occupation", responses.occupation],
    ["Institution", responses.institution],
    ["Attended a masterclass", responses.attendedMasterclass],
    ["Masterclass(es) attended", responses.attendedMasterclassList],
    ["Hope to achieve", responses.goals],
  ].filter(([, value]) => Boolean(value));
}

export default function AdminOrdersPage() {
  const { token } = useAdminAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState<"all" | "checked-in" | "valid">("all");
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let active = true;

    const loadOrders = async (withLoading = false) => {
      if (withLoading) setIsLoading(true);
      try {
        const { orders: data } = await adminApi.listOrders(token);
        if (active) {
          setOrders(data);
          setError(null);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError instanceof Error ? requestError.message : "Could not load orders");
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };

    void loadOrders(true);
    const intervalId = window.setInterval(() => void loadOrders(false), 15000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [token]);

  const filteredOrders = useMemo(() => {
    const term = query.trim().toLowerCase();
    return orders.filter((order) => {
      if (filter !== "all") {
        const matches = (order.tickets ?? []).some((ticket) => ticket.status === filter);
        if (!matches) return false;
      }
      if (!term) return true;
      const haystack = [
        order.customer?.name,
        order.customer?.email,
        order.customer?.phone,
        order.event?.title,
        order.reference,
        order.event?.city,
        order.registrationForm?.responses?.fullName,
        order.registrationForm?.responses?.email,
        order.registrationForm?.responses?.phone,
        order.registrationForm?.responses?.occupation,
        order.registrationForm?.responses?.institution,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [filter, orders, query]);

  const totals = useMemo(() => {
    const grossRevenue = orders.reduce((sum, order) => sum + (order.total ?? 0), 0);
    const paidCount = orders.filter((order) => /paid|success/i.test(order.paymentStatus)).length;
    const checkedIn = orders.reduce(
      (sum, order) => sum + (order.tickets ?? []).filter((ticket) => ticket.status === "checked-in").length,
      0
    );
    return { grossRevenue, paidCount, checkedIn, total: orders.length };
  }, [orders]);

  if (isLoading) return <LoadingState label="Loading orders..." />;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Total orders", value: String(totals.total) },
          { label: "Paid", value: String(totals.paidCount) },
          { label: "Checked in", value: String(totals.checkedIn) },
          { label: "Gross", value: formatCurrency(totals.grossRevenue) },
        ].map((metric) => (
          <div key={metric.label} className="ego-surface p-5">
            <div className="ego-eyebrow">{metric.label}</div>
            <div className="mt-2 text-2xl font-bold tracking-tight text-foreground">{metric.value}</div>
          </div>
        ))}
      </div>

      <div className="ego-surface flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa3b2]" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search reference, customer, or screening"
            className="ego-input pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1 rounded-xl bg-surface-muted p-1">
          {(["all", "valid", "checked-in"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize transition ${
                filter === value
                  ? "bg-surface text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {value === "all" ? "All" : value.replace("-", " ")}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="ego-surface border-l-4 border-l-red-500 px-5 py-4 text-sm font-semibold text-red-600">
          {error}
        </div>
      )}

      <div className="ego-surface overflow-hidden">
        <div className="hidden grid-cols-[1.4fr_1.1fr_0.7fr_1fr_0.7fr] gap-4 border-b border-line bg-surface-muted px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground lg:grid">
          <div>Customer</div>
          <div>Screening</div>
          <div>Total</div>
          <div>Tickets</div>
          <div>Created</div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-surface-muted text-muted-foreground">
              <Receipt size={20} />
            </div>
            <h3 className="text-lg font-bold text-foreground">No orders found</h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              Try clearing your filters or search term.
            </p>
          </div>
        ) : (
          <div className="ego-divide">
            {filteredOrders.map((order) => {
              const tickets = order.tickets ?? [];
              const checkedIn = tickets.filter((ticket) => ticket.status === "checked-in").length;
              const formEntries = registrationEntries(order);
              const lastCheckIn = [...tickets]
                .map((ticket) => ticket.checkedInAt)
                .filter((value): value is string => Boolean(value))
                .sort()
                .at(-1);
              return (
                <div
                  key={order.reference}
                  className="grid gap-4 px-6 py-4 transition hover:bg-surface-muted lg:grid-cols-[1.4fr_1.1fr_0.7fr_1fr_0.7fr] lg:items-center"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-xs font-bold text-[#c2410c]">
                      {initials(order.customer?.name)}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">
                        {order.customer?.name || "Legacy customer"}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">{order.customer?.email || "—"}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className="ego-badge ego-badge-brand">{order.reference}</span>
                        <span className={badgeFor(order.paymentStatus)}>{order.paymentStatus}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {order.event?.title || "Legacy order"}
                    </div>
                    <div className="text-xs text-muted-foreground">{order.event?.city || "Unknown city"}</div>
                  </div>

                  <div className="text-sm font-bold text-foreground">{formatCurrency(order.total)}</div>

                  <div>
                    <div className="text-sm text-foreground">
                      <span className="font-semibold">{tickets.length}</span>{" "}
                      <span className="text-muted-foreground">total</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="ego-badge ego-badge-success">{checkedIn} in</span>
                      <span className="ego-badge ego-badge-warning">{tickets.length - checkedIn} pending</span>
                    </div>
                    {lastCheckIn && (
                      <div className="mt-1 text-[11px] text-[#9aa3b2]">
                        Last verified {formatDateTime(lastCheckIn)}
                      </div>
                    )}
                  </div>

                  <div className="text-sm text-muted-foreground">{formatCompactDate(order.createdAt)}</div>

                  {!!formEntries.length && (
                    <div className="rounded-[8px] border border-line bg-surface-muted px-5 py-4 lg:col-span-5">
                      <div className="mb-4 flex items-center gap-2">
                        <ClipboardList size={16} className="text-aan-bright" />
                        <div>
                          <div className="text-sm font-bold text-foreground">Masterclass registration</div>
                          <div className="text-xs text-muted-foreground">
                            {order.registrationForm?.formTitle ?? "Registration responses"}
                          </div>
                        </div>
                      </div>
                      <dl className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {formEntries.map(([label, value]) => (
                          <div key={label} className={label === "Hope to achieve" ? "md:col-span-2 xl:col-span-3" : ""}>
                            <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                              {label}
                            </dt>
                            <dd className="mt-1 whitespace-pre-wrap text-sm font-medium leading-6 text-foreground">
                              {value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
